import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { IoMdArrowRoundBack } from 'react-icons/io';
import PageForm from '../../templates/PageForm';
import ATable, { ATableHeader } from '../../organisms/ATable';
import { useSignal } from '@preact/signals-react';

const EscavadorDocumentsList: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { batchId, document } = useParams<{ batchId: string; document: string }>();
    
    const cachedBatchData = location.state?.batchData;
    const recommendation = location.state?.recommendation;
    const decodedDocument = decodeURIComponent(document || '');

    const [filterAssunto, setFilterAssunto] = useState('');
    const [filterClasse, setFilterClasse] = useState('');
    const [loading, setLoading] = useState(false);

    const headers = useSignal<ATableHeader[]>([]);
    const rows = useSignal<any[]>([]);

    // Filtrar e ordenar processos
    const documentProcesses = useMemo(() => {
        if (!cachedBatchData?.reports) return [];
        
        let filtered = cachedBatchData.reports.filter((report: any) => 
            report.document === decodedDocument
        );
        
        // Aplicar filtros
        if (filterAssunto) {
            filtered = filtered.filter((report: any) => 
                report.assunto?.toLowerCase().includes(filterAssunto.toLowerCase())
            );
        }
        
        if (filterClasse) {
            filtered = filtered.filter((report: any) => 
                report.classe?.toLowerCase().includes(filterClasse.toLowerCase())
            );
        }
        
        // Ordenar por data_distribuicao DESC (mais recentes primeiro)
        return filtered.sort((a: any, b: any) => {
            const dateA = new Date(a.data_distribuicao || 0);
            const dateB = new Date(b.data_distribuicao || 0);
            return dateB.getTime() - dateA.getTime();
        });
    }, [cachedBatchData, decodedDocument, filterAssunto, filterClasse]);

    useEffect(() => {
        headers.value = [
            { id: 'assunto', title: 'Assunto', canSort: true },
            { id: 'classe', title: 'Classe', canSort: true },
            { id: 'tribunal', title: 'Tribunal', canSort: true },
            { id: 'numero_processo', title: 'Número do Processo', canSort: true },
            { id: 'arquivado', title: 'Arquivado', canSort: true }
        ];

        rows.value = documentProcesses.map((process: any, index: number) => ({
            id: index,
            assunto: process.assunto || <span className="text-gray-400 italic">Não informado</span>,
            classe: process.classe || <span className="text-gray-400 italic">Não informado</span>,
            tribunal: process.tribunal || <span className="text-gray-400 italic">Não informado</span>,
            numero_processo: process.numero_processo || <span className="text-gray-400 italic">Não informado</span>,
            arquivado: renderArquivado(process.arquivado),
            onClick: () => handleProcessClick(process)
        }));
    }, [documentProcesses]);

    const renderArquivado = (value: any) => {
        if (value === null || value === undefined) {
            return <span className="text-gray-400 italic">Não informado</span>;
        }
        return (
            <span className={`px-2 py-1 rounded text-xs font-medium ${
                value ? 'bg-gray-100 text-gray-800' : 'bg-green-100 text-green-800'
            }`}>
                {value ? 'Sim' : 'Não'}
            </span>
        );
    };

    const handleProcessClick = (process: any) => {
        navigate(`/recommendation-engine/document-details/${batchId}`, {
            state: {
                document: decodedDocument,
                processNumber: process.numero_processo,
                recommendation,
                batchData: cachedBatchData
            }
        });
    };

    const handleBack = () => {
        navigate(`/recommendation-engine/recommendations/${batchId}`, {
            state: { batchData: cachedBatchData }
        });
    };

    const handleClearFilters = () => {
        setFilterAssunto('');
        setFilterClasse('');
    };

    if (!cachedBatchData) {
        return (
            <PageForm
                pageTitle="Erro ao Carregar Processos"
                apiVersion="1.0"
                isLoading={false}
                breadcrumb={[]}
                controlSlot={<></>}
            >
                <div className="text-center py-8">
                    <div className="text-red-600 mb-4">❌ Dados do lote não encontrados</div>
                    <button
                        onClick={handleBack}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                    >
                        Voltar para Recomendações
                    </button>
                </div>
            </PageForm>
        );
    }

    return (
        <PageForm
            pageTitle={`Processos Judiciais - ${decodedDocument}`}
            apiVersion="1.0"
            isLoading={loading}
            breadcrumb={[
                { label: 'Recommendation Engine', url: '/recommendation-engine' },
                { label: 'Recomendações', url: `/recommendation-engine/recommendations/${batchId}` },
                { label: 'Processos', url: '#' }
            ]}
            controlSlot={<></>}
        >
            <div className="mb-6">
                <button
                    onClick={handleBack}
                    className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
                >
                    <IoMdArrowRoundBack />
                    Voltar para Recomendações
                </button>

                {/* Informações do Documento */}
                <div className="bg-gray-50 p-4 rounded-lg mb-6 mt-4">
                    <h3 className="font-semibold mb-2">Informações do Documento</h3>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                            <span className="text-gray-600">Documento:</span>
                            <div className="font-medium">{decodedDocument}</div>
                        </div>
                        <div>
                            <span className="text-gray-600">Nome:</span>
                            <div className="font-medium">{recommendation?.entityName || 'N/A'}</div>
                        </div>
                        <div>
                            <span className="text-gray-600">Total de Processos:</span>
                            <div className="font-medium">{documentProcesses.length}</div>
                        </div>
                        <div>
                            <span className="text-gray-600">Provider:</span>
                            <div className="font-medium">{cachedBatchData.provider || 'N/A'}</div>
                        </div>
                    </div>
                </div>

                {/* Filtros */}
                <div className="mb-4 flex gap-4">
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Filtrar por Assunto
                        </label>
                        <input
                            type="text"
                            value={filterAssunto}
                            onChange={(e) => setFilterAssunto(e.target.value)}
                            placeholder="Digite para filtrar..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div className="flex-1">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Filtrar por Classe
                        </label>
                        <input
                            type="text"
                            value={filterClasse}
                            onChange={(e) => setFilterClasse(e.target.value)}
                            placeholder="Digite para filtrar..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    {(filterAssunto || filterClasse) && (
                        <div className="flex items-end">
                            <button
                                onClick={handleClearFilters}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                            >
                                Limpar Filtros
                            </button>
                        </div>
                    )}
                </div>

                {/* Mensagem de filtros ativos */}
                {(filterAssunto || filterClasse) && (
                    <div className="mb-4 text-sm text-gray-600">
                        Exibindo {documentProcesses.length} processo(s) filtrado(s)
                    </div>
                )}
            </div>

            {documentProcesses.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                    {filterAssunto || filterClasse ? (
                        <>
                            <p className="mb-2">Nenhum processo corresponde aos filtros aplicados</p>
                            <button
                                onClick={handleClearFilters}
                                className="text-blue-600 hover:text-blue-800 underline"
                            >
                                Limpar filtros
                            </button>
                        </>
                    ) : (
                        <p>Nenhum processo encontrado para este documento</p>
                    )}
                </div>
            ) : (
                <ATable headers={headers.value}>
                    {rows.value.map((row, index) => (
                        <tr 
                            key={index} 
                            className="hover:bg-gray-50 cursor-pointer"
                            onClick={row.onClick}
                        >
                            {headers.value.map((header) => (
                                <td key={header.id} className="px-4 py-2 border-b">
                                    {row[header.id]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </ATable>
            )}
        </PageForm>
    );
};

export default EscavadorDocumentsList;
